import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Mail, Phone, MapPin, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { format } from 'date-fns';
import { SEO } from '../components/SEO';

const API = `${window.location.origin}/api`;

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const eventTypes = [
  'Corporate Event',
  'Wedding / Social Celebration',
  'Private Event',
  'Other'
];

const guestCounts = [
  'Under 50',
  '50–100',
  '100–200',
  '200+'
];

const budgetRanges = [
  'Under $10,000',
  '$10,000 – $25,000',
  '$25,000 – $50,000',
  '$50,000 – $100,000',
  '$100,000+'
];

const supportLevels = [
  'Strategy Session ($300)',
  'Foundation Package',
  'Signature Package',
  'Elite Concierge',
  'Vendor Sourcing',
  'Budget Audit',
  'Not Sure Yet'
];

const strategySessionOptions = [
  'Yes — ready to move forward',
  "I'd like to learn more",
  'Not at this time'
];

const priorities = [
  'Having a clear plan and direction',
  'Finding the right vendors',
  'Making confident decisions',
  'Avoiding costly mistakes',
  'Saving time and reducing stress'
];

const howHeardOptions = [
  'Referral',
  'Social Media',
  'Website',
  'Vendor Recommendation',
  'Other'
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: '',
    event_date: null,
    event_location: '',
    guest_count: '',
    budget_range: '',
    services_needed: [],
    strategy_session_ready: '',
    priorities: [],
    message: '',
    how_heard: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();

  // Prefill services_needed from ?services=... query param (comma-separated).
  // Used so CTAs across the site can pre-select the user's chosen package.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const servicesParam = params.get('services');
    if (!servicesParam) return;
    const requested = servicesParam.split(',').map(s => s.trim()).filter(Boolean);
    // Only keep values that match our supported supportLevels list
    const valid = requested.filter(s => supportLevels.includes(s));
    if (valid.length > 0) {
      setFormData(prev => ({
        ...prev,
        services_needed: Array.from(new Set([...prev.services_needed, ...valid])),
      }));
    }
  }, [location.search]);

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services_needed: prev.services_needed.includes(service)
        ? prev.services_needed.filter(s => s !== service)
        : [...prev.services_needed, service]
    }));
  };

  const handlePriorityToggle = (priority) => {
    setFormData(prev => ({
      ...prev,
      priorities: prev.priorities.includes(priority)
        ? prev.priorities.filter(p => p !== priority)
        : [...prev.priorities, priority]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/inquiries`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        event_type: formData.event_type,
        event_date: formData.event_date ? format(formData.event_date, 'yyyy-MM-dd') : null,
        budget_range: formData.budget_range,
        services_needed: formData.services_needed,
        message: `Location: ${formData.event_location || 'Not specified'}\nGuest Count: ${formData.guest_count || 'Not specified'}\nStrategy Session Ready: ${formData.strategy_session_ready || 'Not specified'}\nPriorities: ${formData.priorities.join(', ') || 'None selected'}\nHow Heard: ${formData.how_heard || 'Not specified'}\n\n${formData.message}`
      });
      setSubmitted(true);
      toast.success('Your inquiry has been submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-32 bg-[#050505]" data-testid="contact-success">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Success Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="w-20 h-20 mx-auto mb-8 border border-[#C6A86A] flex items-center justify-center">
              <Check className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">Inquiry Received</h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-xl mx-auto">
              Thank you for reaching out. We&apos;ll review your submission within 24–48 hours.
            </p>
            <p className="text-white/70 leading-relaxed max-w-xl mx-auto mt-4">
              If aligned, you&apos;ll receive an invitation to book your White Glove Strategy Session, where we&apos;ll define your direction and next steps.
            </p>
          </motion.div>

          {/* What Happens Next */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#0a0a0a] p-10 md:p-12 border border-white/10 mb-12"
          >
            <h3 className="font-heading text-2xl text-white mb-10 text-center">What happens next</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <span className="text-5xl font-heading text-[#D4AF37]/30">01</span>
                <h4 className="font-heading text-lg text-white mt-4 mb-2">Inquiry Review</h4>
                <p className="text-white/60 text-sm leading-relaxed">
                  We review your event details to ensure alignment and determine the best next step.
                </p>
              </div>
              <div className="text-center">
                <span className="text-5xl font-heading text-[#D4AF37]/30">02</span>
                <h4 className="font-heading text-lg text-white mt-4 mb-2">Strategy Session Invitation</h4>
                <p className="text-white/60 text-sm leading-relaxed">
                  If aligned, you&apos;ll receive a link to book your White Glove Strategy Session.
                </p>
              </div>
              <div className="text-center">
                <span className="text-5xl font-heading text-[#D4AF37]/30">03</span>
                <h4 className="font-heading text-lg text-white mt-4 mb-2">Clear Direction Forward</h4>
                <p className="text-white/60 text-sm leading-relaxed">
                  From there, we define your event strategy and recommend the right level of support.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="text-center">
            <a href="/" className="inline-flex items-center justify-center px-8 py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors">
              Return Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <SEO
        title="Book a Strategy Session | White Glove Event Concierge"
        description="Start with a $300 Strategy Session for clarity on your event budget, vendor direction, and next steps in Nashville."
      />
      {/* HERO SECTION */}
      <section className="pt-32 pb-16 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">Get Started</span>
            <h1 className="font-heading text-4xl sm:text-5xl text-white leading-tight mt-4 mb-6">
              Let&apos;s start with clarity—<br />and the right{' '}
              <span className="italic text-[#D4AF37]">strategy</span>
            </h1>
            <div className="space-y-4 text-white/70 text-lg leading-relaxed">
              <p>
                To ensure every client receives the level of guidance and attention we&apos;re known for, we begin with a short inquiry process.
              </p>
              <p>
                All services begin with a <strong className="text-white">White Glove Strategy Session ($300)</strong>, designed to bring clarity, structure, and direction to your event.
              </p>
            </div>
            <p className="text-white/50 text-sm mt-8 italic">
              With over 20 years of experience in event operations, venue leadership, and client experience, our process is designed to bring confidence to every decision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FORM SECTION */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp}>
            <div className="bg-[#0a0a0a] p-8 md:p-12 border border-white/10">
              <form onSubmit={handleSubmit} className="space-y-12" data-testid="inquiry-form">
                
                {/* YOUR INFORMATION */}
                <div>
                  <h3 className="font-heading text-xl text-white mb-8 pb-4 border-b border-white/10">Your Information</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                        placeholder="Your name"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                        placeholder="(615) 555-0000"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* EVENT DETAILS */}
                <div>
                  <h3 className="font-heading text-xl text-white mb-8 pb-4 border-b border-white/10">Event Details</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Event Type</label>
                      <Select
                        value={formData.event_type}
                        onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                      >
                        <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 focus:ring-0 focus:border-[#D4AF37] text-white h-auto" data-testid="select-event-type">
                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/10">
                          {eventTypes.map((type) => (
                            <SelectItem key={type} value={type} className="text-white/70 focus:bg-[#050505] focus:text-white">
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Estimated Event Date</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="w-full bg-transparent border-b border-white/20 py-3 text-left text-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                            data-testid="select-date"
                          >
                            {formData.event_date ? format(formData.event_date, 'MMMM d, yyyy') : <span className="text-[#B8AEA1]">Select a date</span>}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="bg-[#0a0a0a] border-white/10 p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.event_date}
                            onSelect={(date) => setFormData({ ...formData, event_date: date })}
                            disabled={(date) => date < new Date()}
                            className="bg-[#0a0a0a]"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Event Location</label>
                      <input
                        type="text"
                        value={formData.event_location}
                        onChange={(e) => setFormData({ ...formData, event_location: e.target.value })}
                        className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors"
                        placeholder="City, State or Venue"
                      />
                    </div>
                    <div>
                      <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Estimated Guest Count</label>
                      <Select
                        value={formData.guest_count}
                        onValueChange={(value) => setFormData({ ...formData, guest_count: value })}
                      >
                        <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 focus:ring-0 focus:border-[#D4AF37] text-white h-auto">
                          <SelectValue placeholder="Select guest count" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/10">
                          {guestCounts.map((count) => (
                            <SelectItem key={count} value={count} className="text-white/70 focus:bg-[#050505] focus:text-white">
                              {count}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">What is your estimated overall event investment?</label>
                      <Select
                        value={formData.budget_range}
                        onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                      >
                        <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 focus:ring-0 focus:border-[#D4AF37] text-white h-auto" data-testid="select-budget">
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/10">
                          {budgetRanges.map((range) => (
                            <SelectItem key={range} value={range} className="text-white/70 focus:bg-[#050505] focus:text-white">
                              {range}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* LEVEL OF SUPPORT */}
                <div>
                  <h3 className="font-heading text-xl text-white mb-2 pb-4 border-b border-white/10">Level of Support</h3>
                  <p className="text-white/60 text-sm mb-6">Select the level of support you&apos;re most interested in</p>
                  <div className="flex flex-wrap gap-3">
                    {supportLevels.map((service) => (
                      <button
                        key={service}
                        type="button"
                        onClick={() => handleServiceToggle(service)}
                        className={`text-xs uppercase tracking-widest px-4 py-3 border transition-colors ${
                          formData.services_needed.includes(service)
                            ? 'border-[#C6A86A] bg-[#D4AF37] text-white'
                            : 'border-white/20 text-white/60 hover:border-[#1B1B1B] hover:text-white'
                        }`}
                      >
                        {service}
                      </button>
                    ))}
                  </div>
                </div>

                {/* INVESTMENT ALIGNMENT */}
                <div>
                  <h3 className="font-heading text-xl text-white mb-8 pb-4 border-b border-white/10">Investment Alignment</h3>
                  <label className="block text-white/70 text-sm mb-4">Are you comfortable beginning with a $300 Strategy Session?</label>
                  <div className="space-y-3">
                    {strategySessionOptions.map((option) => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${
                          formData.strategy_session_ready === option
                            ? 'border-[#C6A86A] bg-[#D4AF37]'
                            : 'border-white/20 group-hover:border-[#1B1B1B]'
                        }`}>
                          {formData.strategy_session_ready === option && <Check size={12} className="text-white" />}
                        </div>
                        <input
                          type="radio"
                          name="strategy_session_ready"
                          value={option}
                          checked={formData.strategy_session_ready === option}
                          onChange={(e) => setFormData({ ...formData, strategy_session_ready: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-white/70 text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* EVENT INSIGHT */}
                <div>
                  <h3 className="font-heading text-xl text-white mb-2 pb-4 border-b border-white/10">Event Insight</h3>
                  <label className="block text-white/70 text-sm mb-4">What feels most important to you right now?</label>
                  <div className="space-y-3">
                    {priorities.map((priority) => (
                      <label key={priority} className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${
                          formData.priorities.includes(priority)
                            ? 'border-[#C6A86A] bg-[#D4AF37]'
                            : 'border-white/20 group-hover:border-[#1B1B1B]'
                        }`}>
                          {formData.priorities.includes(priority) && <Check size={12} className="text-white" />}
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.priorities.includes(priority)}
                          onChange={() => handlePriorityToggle(priority)}
                          className="sr-only"
                        />
                        <span className="text-white/70 text-sm">{priority}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* TELL US MORE */}
                <div>
                  <h3 className="font-heading text-xl text-white mb-8 pb-4 border-b border-white/10">Tell Us More</h3>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full bg-transparent border border-white/20 p-4 text-white placeholder:text-[#B8AEA1] focus:outline-none focus:border-[#D4AF37] transition-colors min-h-[180px] resize-none"
                    placeholder="Tell us about your event, your priorities, and where you feel you need the most support. The more context you share, the more tailored our response can be."
                    data-testid="input-message"
                  />
                </div>

                {/* ADDITIONAL DETAILS */}
                <div>
                  <h3 className="font-heading text-xl text-white mb-8 pb-4 border-b border-white/10">Additional Details <span className="text-[#B8AEA1] font-normal">(optional)</span></h3>
                  <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">How did you hear about us?</label>
                  <Select
                    value={formData.how_heard}
                    onValueChange={(value) => setFormData({ ...formData, how_heard: value })}
                  >
                    <SelectTrigger className="w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 focus:ring-0 focus:border-[#D4AF37] text-white h-auto">
                      <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                      {howHeardOptions.map((option) => (
                        <SelectItem key={option} value={option} className="text-white/70 focus:bg-[#050505] focus:text-white">
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* SUBMIT */}
                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 px-10 py-4 bg-[#1B1B1B] text-white text-sm uppercase tracking-widest hover:bg-[#111111] transition-colors disabled:opacity-50"
                    data-testid="submit-btn"
                  >
                    {loading ? (
                      <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <>
                        Submit & Continue to Next Steps <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Contact Info Below Form */}
          <motion.div {...fadeInUp} className="mt-12 text-center">
            <p className="text-white/60 text-sm mb-4">Questions before submitting?</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm">
              <a href="mailto:info@whitegloveeventconcierge.com" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                <Mail size={16} className="text-[#D4AF37]" />
                info@whitegloveeventconcierge.com
              </a>
              <a href="tel:+16156690576" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                <Phone size={16} className="text-[#D4AF37]" />
                615.669.0576
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
